/**
 * Voice — WinterMute's typed subtitle.
 *
 * Screen-space and flat, on purpose. This is a voice, not an object in the room.
 * World-anchored point text (see `visual/Text/`) is reserved for fragments,
 * which are things. Keeping the two registers distinct is a decision, not an
 * inconsistency: when WinterMute speaks the words belong to the interface, and
 * when a fragment speaks the words belong to the space.
 *
 * Lines type in sequence and previous lines stay, dimmed, so a beat reads as a
 * short paragraph rather than a teleprompter.
 */

import { useEffect, useRef, useState } from 'react';
import { LINE_PAUSE_MS, TYPE_MS } from './script.js';

export interface VoiceProps {
  lines: string[];
  /** Fires once every line has finished typing. */
  onComplete?: () => void;
  /** Delay before the first character. */
  startDelayMs?: number;
}

export function Voice({ lines, onComplete, startDelayMs = 0 }: VoiceProps) {
  const [done, setDone] = useState<string[]>([]);
  const [typing, setTyping] = useState('');
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    setDone([]);
    setTyping('');
    let cancelled = false;
    const timers: number[] = [];

    const run = async () => {
      await wait(startDelayMs, timers);
      for (let li = 0; li < lines.length; li++) {
        const line = lines[li]!;
        for (let i = 1; i <= line.length; i++) {
          if (cancelled) return;
          setTyping(line.slice(0, i));
          // Punctuation gets a beat of its own — it is most of what makes typed
          // text sound like speech rather than a printer.
          const ch = line[i - 1]!;
          await wait(TYPE_MS * (/[.,;:—]/.test(ch) ? 7 : 1), timers);
        }
        if (cancelled) return;
        setDone((d) => [...d, line]);
        setTyping('');
        if (li < lines.length - 1) await wait(LINE_PAUSE_MS, timers);
      }
      if (!cancelled) completeRef.current?.();
    };
    void run();

    return () => {
      cancelled = true;
      for (const t of timers) window.clearTimeout(t);
    };
  }, [lines, startDelayMs]);

  return (
    <div className="voice" aria-live="polite">
      {done.map((l, i) => (
        <p key={i} className="voice-line past">
          {l}
        </p>
      ))}
      {typing && (
        <p className="voice-line current">
          {typing}
          <span className="voice-caret" />
        </p>
      )}
    </div>
  );
}

function wait(ms: number, timers: number[]): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((res) => timers.push(window.setTimeout(res, ms)));
}
